import { HeyUtils } from '../core';
import { config } from '../config';
import { CallResponse } from '../types';

export class BaseService {
  protected heyUtils: HeyUtils;

  constructor(heyUtils: HeyUtils) {
    this.heyUtils = heyUtils;
  }

  protected async callHeyApiUser(method: string, url: string, body: any): Promise<CallResponse> {
    await this.heyUtils.waitInit();
    if (!this.heyUtils.userAccessToken) {
      return { success: false, error: 'No user token found' };
    }
    
    if (Date.now() > this.heyUtils.expiresAt) {
      console.debug("🔄 Token expired, attempting refresh");
      let isRefreshed = await this.callRefreshToken();
      if (!isRefreshed) {
        console.error("❌ Token refresh failed");
        return { success: false, error: 'Failed to refresh user token' };
      }
      console.debug("✅ Token refreshed successfully");
    }
    
    return await this.callHeyApi(method, url, this.heyUtils.userAccessToken, body);
  }

  protected async callHeyApiTech(method: string, url: string, body: any): Promise<CallResponse> {
    await this.heyUtils.waitInit();
    if (!this.heyUtils.techToken || Date.now() > this.heyUtils.techTokenExpires) {
      console.debug('No technical token found, getting one...');
      if (!await this.getTechnicalToken()) {
        return { success: false, error: 'Failed to get technical token' };
      }
    }
    
    return await this.callHeyApi(method, url, this.heyUtils.techToken, body);
  }

  protected async callHeyApiOAGTech(method: string, url: string, body: any): Promise<CallResponse> {
    await this.heyUtils.waitInit();
    if (!this.heyUtils.oagToken || Date.now() > this.heyUtils.oagTokenExpires) {
      console.debug('No oag token found, getting one...');
      if (!await this.getOagToken()) {
        return { success: false, error: 'Failed to get oag token' };
      }
    }
    console.debug('OAG Token...', this.heyUtils.oagToken);
    return await this.callHeyApiOag(method, url, this.heyUtils.oagToken, body);
  }

  protected async callHeyApiOag(method: string, url: string, token: string, body: any): Promise<CallResponse> {
    await this.heyUtils.waitInit();
    if (this.heyUtils.getConfig().baseUrlOag === '') {
      console.error('baseUrlOag is not set in config');
      return { success: false, error: 'Unable to find Hey base configuration. Connectivity cannot be established' };
    }
    const fullUrl = `${this.heyUtils.getConfig().baseUrlOag}${url}`;
    // console.debug(`🌐 API CALL: ${method} ${fullUrl}`, body ? JSON.stringify(body, null, 2) : "No body");
    
    const response = await fetch(fullUrl, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Forwarded-For': this.heyUtils.getConfig().clientName,
        'Authorization': token ? `Bearer ${token}` : ''
      },
      body: body ? JSON.stringify(body) : null
    });

    if (!response.ok) {
      let errorText = "";
      let errorData = null;
      
      try {
        let repBody = await response.json();
        console.debug(`❌ API ERROR RESPONSE [${response.status}]:`, repBody);
        errorData = repBody;
        
        // Handle errors array format
        if (repBody.errors) {
          for (let error of repBody.errors) {
            if (error.detail) {
              errorText += error.detail + '. ';
            }
          }
        }
        
        // Check for specific profile mismatch error patterns in the response
        const responseStr = JSON.stringify(repBody);
        if (responseStr.includes("UNAVAILABLE_PNR") && responseStr.includes("NOT_1A_PNR")) {
          console.warn('⚠️ [BaseService] Potential profile mismatch found in error response');
          // We'll extract and flag this error in the calling service
        }
      } catch (e) {
        console.debug('Error parsing response as json:', e);
      }
      
      if (!errorText) {
        errorText = "Unknown error (status code: " + response.status + ")";
      }
      
      if (response.status === 401 && token) {
        console.error('Unauthorized access - OAG token might be expired. Getting new one');
        await this.getOagToken();
        errorText = "Technical error. Unauthorized access.";
      }
      
      console.error('Request failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
        url: fullUrl
      });
      
      return {
        success: false,
        status: response.status,
        error: errorText,
        data: errorData  // Return the full error body for detailed inspection
      };
    }

    let data = null;
    try {
      data = await response.json();
      console.debug(`✅ API SUCCESS RESPONSE [${response.status}]:`, JSON.stringify(data, null, 2));
    } catch (e) {
      console.error('Error parsing response as json:', e);
      data = {};
    }
    return {
      success: true,
      data: data
    };
  }

  protected async callHeyApi(method: string, url: string, token: string, body: any): Promise<CallResponse> {
    if (this.heyUtils.getConfig().baseUrl === '') {
      console.error('baseUrl is not set in config');
      return { success: false, error: 'Unable to find Hey base configuration. Connectivity cannot be established' };
    }
    const fullUrl = `${this.heyUtils.getConfig().baseUrl}${url}`;
    // console.debug(`🌐 API CALL: ${method} ${url}`, body ? JSON.stringify(body, null, 2) : "No body");
    
    const response = await fetch(fullUrl, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Forwarded-For': this.heyUtils.getConfig().clientName,
        'Authorization': token ? `Bearer ${token}` : ''
      },
      body: body ? JSON.stringify(body) : null
    });

    if (!response.ok) {
      let errorText = "";
      let errorData = null;
      
      try {
        let repBody = await response.json();
        console.debug(`❌ API ERROR RESPONSE [${response.status}]:`, repBody);
        errorData = repBody;
        
        // Handle errors array format
        if (repBody.errors) {
          for (let error of repBody.errors) {
            if (error.detail) {
              errorText += error.detail + '. ';
            }
          }
        }
        
        // Check for specific profile mismatch error patterns in the response
        const responseStr = JSON.stringify(repBody);
        if (responseStr.includes("UNAVAILABLE_PNR") && responseStr.includes("NOT_1A_PNR")) {
          console.warn('⚠️ [BaseService] Potential profile mismatch found in error response');
          // We'll extract and flag this error in the calling service
        }
      } catch (e) {
        console.debug('Error parsing response as json:', e);
      }
      
      if (!errorText) {
        errorText = "Unknown error (status code: " + response.status + ")";
      }
      
      if (response.status === 401 && token) {
        console.error('Unauthorized access - token might be expired. Getting new one');
        await this.getTechnicalToken();
        errorText = "Technical error. Unauthorized access.";
      }
      
      console.error('Request failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
        url: fullUrl
      });
      
      return {
        success: false,
        status: response.status,
        error: errorText,
        data: errorData  // Return the full error body for detailed inspection
      };
    }

    let data = null;
    try {
      data = await response.json();
      console.debug(`✅ API SUCCESS RESPONSE [${response.status}]:`);
      // console.debug(JSON.stringify(data, null, 2));
    } catch (e) {
      console.error('Error parsing response as json:', e);
      data = {};
    }
    return {
      success: true,
      data: data
    };
  }

  protected async getTechnicalToken(): Promise<boolean> {
    if (this.heyUtils.techToken && Date.now() < this.heyUtils.techTokenExpires) {
      console.debug('Technical token already exists and is valid');
      return true;
    }

    let reply = await this.callHeyApi('POST', '/v3/authenticate', '', {
      login: this.heyUtils.getConfig().techLogin,
      password: this.heyUtils.getConfig().techPassword
    });

    if (!reply.success || !reply?.data?.data.idToken) {
      console.error(`Technical authentication failed: ${reply.status} ${reply.error}`);
      return false;
    }

    this.heyUtils.techToken = reply.data.data.idToken;
    this.heyUtils.techTokenExpires = Date.now() + 1000 * 60 * 60; // 1 hour
    try {
      localStorage.setItem(config.LS_KEY_TECH_TOKEN, this.heyUtils.techToken);
    } catch (error) {
      console.error('Error saving flight details to localStorage:', error);
      localStorage.clear();
    }
    return true;
  }
  protected async getOagToken(): Promise<boolean> {
    if (this.heyUtils.oagToken && Date.now() < this.heyUtils.oagTokenExpires) {
      console.debug('Oag token already exists and is valid');
      return true;
    }
    
    let reply = await this.callHeyApiOag('POST', '/api/authenticate', '', {
      username: this.heyUtils.getConfig().oagLogin,
      password: this.heyUtils.getConfig().oagPassword
    });

    if (!reply.success || !reply?.data?.id_token) {
      console.error(`Oag authentication failed: ${reply.status} ${reply.error}`);
      return false;
    }

    this.heyUtils.oagToken = reply.data.id_token;
    this.heyUtils.oagTokenExpires = Date.now() + 1000 * 60 * 60; // 1 hour
    try{
      localStorage.setItem(config.LS_KEY_OAG_TOKEN, this.heyUtils.oagToken);
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      localStorage.clear();
    }
    return true;
  }

  protected async callRefreshToken(): Promise<boolean> {
    console.debug('Token has expired, attempting to refresh...');
    if (!await this.getTechnicalToken()) {
      console.error('Failed to get technical token');
      return false;
    }

    const refreshUrl = `/v3/user/${this.heyUtils.user_id}/refreshToken`;
    console.debug('Refreshing token at:', refreshUrl);
    const refreshResponse = await this.callHeyApiTech('POST', refreshUrl, {
      refreshToken: this.heyUtils.refreshToken
    });

    if (!refreshResponse.success) {
      this.heyUtils.userAccessToken = '';
      this.heyUtils.refreshToken = '';
      this.heyUtils.expiresAt = 0;
      localStorage.removeItem(config.LS_KEY_TOKEN);
      localStorage.removeItem(config.LS_KEY_REFRESH_TOKEN);
      localStorage.removeItem(config.LS_KEY_TOKEN_EXPIRES);
      return false;
    }

    this.heyUtils.userAccessToken = refreshResponse.data.data.idToken;
    this.heyUtils.expiresAt = Date.now() + 1000 * 60 * 60 * 24;
    try {
      localStorage.setItem(config.LS_KEY_TOKEN, this.heyUtils.userAccessToken);
      localStorage.setItem(config.LS_KEY_TOKEN_EXPIRES, String(this.heyUtils.expiresAt));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      localStorage.clear();
    }
    return true;
  }
}
